import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { COMPANY, NAV_LINKS } from "@/mock";

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 w-full backdrop-blur-xl transition-all duration-300 ${
        scrolled
          ? "bg-brand-bg/85 border-b border-brand-border"
          : "bg-brand-bg/60 border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
        {/* Logo */}
        <Link
          to="/"
          data-testid="logo-link"
          className="flex items-center gap-3 group"
        >
          <div className="w-9 h-9 bg-brand-ink flex items-center justify-center">
            <span className="font-heading text-brand-bg font-black text-lg leading-none">
              J
            </span>
          </div>
          <div className="leading-none">
            <div className="font-heading font-black text-xl tracking-tight text-brand-ink">
              {COMPANY.shortName}
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-brand-inkSoft mt-0.5">
              Contracting
            </div>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav
          data-testid="desktop-nav"
          className="hidden lg:flex items-center gap-10"
        >
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              data-testid={`nav-${link.label.toLowerCase()}`}
              className={({ isActive }) =>
                `nav-link text-sm font-medium tracking-wide text-brand-ink/80 hover:text-brand-ink transition ${
                  isActive ? "active text-brand-ink" : ""
                }`
              }
              end={link.path === "/"}
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        {/* Right Actions */}
        <div className="flex items-center gap-3">
          <a
            href={`tel:${COMPANY.phone.replace(/[^0-9+]/g, "")}`}
            data-testid="header-phone"
            className="hidden md:flex items-center gap-2 text-sm font-medium text-brand-ink hover:text-brand-cta transition"
          >
            <Phone className="w-4 h-4" strokeWidth={1.75} />
            {COMPANY.phone}
          </a>
          <Link
            to="/contact"
            data-testid="header-cta-quote"
            className="hidden md:inline-flex items-center gap-2 px-5 py-3 bg-brand-cta text-white text-xs font-bold uppercase tracking-[0.16em] hover:bg-brand-ctaHover transition-colors"
          >
            Get a Quote
          </Link>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            className="lg:hidden p-2 text-brand-ink hover:text-brand-cta transition"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {open && (
        <div
          className="lg:hidden border-t border-brand-border bg-brand-bg"
          data-testid="mobile-nav"
        >
          <nav className="flex flex-col px-6 py-6 gap-1">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `py-3 text-base font-medium border-b border-brand-border/60 ${
                    isActive ? "text-brand-cta" : "text-brand-ink"
                  }`
                }
                end={link.path === "/"}
              >
                {link.label}
              </NavLink>
            ))}
            <a
              href={`tel:${COMPANY.phone.replace(/[^0-9+]/g, "")}`}
              data-testid="mobile-phone"
              className="py-3 text-base font-medium text-brand-inkSoft border-b border-brand-border/60 flex items-center gap-2"
            >
              <Phone className="w-4 h-4" /> {COMPANY.phone}
            </a>
            <Link
              to="/contact"
              data-testid="mobile-cta-quote"
              className="mt-4 inline-flex items-center justify-center px-5 py-4 bg-brand-cta text-white text-xs font-bold uppercase tracking-[0.16em]"
            >
              Get a Free Quote
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
